/* eslint-disable max-params -- these linting errors come up constantly with reducers */
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import isNil from "lodash/isNil";

import { TreatmentProgram } from "@/constants/User";
import { isUserInTreatment } from "@/hooks/usePatientStatus";
import type { RootState } from "@/store";
import { useAppSelector } from "@/store/hooks";
import { extraReducersFactory } from "@/store/reducers/reset";

/**
 * Represents the state of the AppUser slice in the Redux store.
 * @interface AppUserSliceState
 * @property {boolean} isPatient
 * @property {boolean} isSupport - Convenience flag to determine if the user is a support user, derived from isPatient
 * @property {LinkedPatientProfile[]} linkedPatients - Linked patients for a support user. Undefined for patients.
 * @extends BaseUserProps
 */
interface AppUserSliceState extends BaseUserProps {
  isInTreatment?: boolean;
  isSupport: boolean;
}

const initialState: AppUserSliceState = {
  externalId: "",
  firstName: "",
  hasGivenRecordingConsent: false,
  isPatient: false,
  isSupport: false,
  lastName: "",
};

const appUserSlice = createSlice({
  // NOTE: extraReducers is required for resetAllReduxSlices to work as expected
  extraReducers: extraReducersFactory(initialState),
  initialState,
  name: "appUser",
  reducers: {
    reset: () => initialState,
    setAppSupportLinkedPatients: (
      state,
      action: PayloadAction<LinkedPatientProfile[]>,
    ) => {
      return { ...state, linkedPatients: action.payload };
    },
    setAppUserBasicInfo: (
      state,
      action: PayloadAction<Partial<AppUserSliceState>>,
    ) => {
      return {
        ...state,
        ...action.payload,
        isSupport: action.payload.isPatient === false,
      };
    },
    setAppUserUniversalStatusCode: (
      state,
      action: PayloadAction<UniversalStatusCode>,
    ) => {
      return { ...state, universalStatusCode: action.payload };
    },
    setIsAppUserInTreatment: (state, action: PayloadAction<boolean>) => {
      return { ...state, isInTreatment: action.payload };
    },
  },
});

const {
  setAppSupportLinkedPatients: setAppSupportLinkedPatientsAction,
  setAppUserBasicInfo: setAppUserBasicInfoAction,
  setAppUserUniversalStatusCode: setAppUserUniversalStatusCodeAction,
  setIsAppUserInTreatment: setIsAppUserInTreatmentAction,
} = appUserSlice.actions;

const selectAppUserLinkedPatients = (state: RootState) =>
  state.appUser.linkedPatients;

const selectAppUserProxyPatients = createSelector(
  [selectAppUserLinkedPatients],
  (linkedPatients) => linkedPatients?.filter((p) => p.isProxy),
);

const selectAppUserPatientsInAdultProgram = createSelector(
  [selectAppUserLinkedPatients],
  (linkedPatients) =>
    linkedPatients?.filter(
      (p) =>
        p.programName === TreatmentProgram.EquipAdult &&
        !isNil(p.universalStatusCode) &&
        isUserInTreatment(p.universalStatusCode),
    ),
);

const selectAppUserProxyPatientsInTreatment = createSelector(
  [selectAppUserProxyPatients],
  (proxyPatients) =>
    proxyPatients?.filter(
      (p) =>
        !isNil(p.universalStatusCode) &&
        isUserInTreatment(p.universalStatusCode),
    ),
);

const selectAppUser = (state: RootState) => state.appUser;

const useAppUserSelector = () => {
  return useAppSelector(selectAppUser);
};

const selectAppUserExternalId = (state: RootState) => state.appUser.externalId;
const selectAppUserIsPatient = (state: RootState) => state.appUser.isPatient;
const selectAppUserIsSupport = (state: RootState) => state.appUser.isSupport;

const useAppUserProxyPatientsSelector = () => {
  return useAppSelector(selectAppUserProxyPatients);
};

const useAppUserProxyPatientsInTreatmentSelector = () => {
  return useAppSelector(selectAppUserProxyPatientsInTreatment);
};

const useAppUserLinkedPatientsSelector = () => {
  return useAppSelector(selectAppUserLinkedPatients);
};

const useAppUserPatientsInAdultProgramSelector = () => {
  return useAppSelector(selectAppUserPatientsInAdultProgram);
};

export {
  initialState,
  selectAppUserExternalId,
  selectAppUserIsPatient,
  selectAppUserIsSupport,
  selectAppUserLinkedPatients,
  setAppSupportLinkedPatientsAction,
  setAppUserBasicInfoAction,
  setAppUserUniversalStatusCodeAction,
  setIsAppUserInTreatmentAction,
  useAppUserLinkedPatientsSelector,
  useAppUserPatientsInAdultProgramSelector,
  useAppUserProxyPatientsInTreatmentSelector,
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
};
export type { AppUserSliceState };
export default appUserSlice.reducer;
